import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_NATIVE_DATE_FORMATS,
  MatNativeDateModule,
  MatRippleModule,
  NativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LocationV2Service } from '@services/location-v2.service';
import { SelectDropDownModule } from 'ngx-select-dropdown';
import { take } from 'rxjs';
import { ServiceRequestService } from 'src/app/service/service-request.service';

import { OnlyTimeDirective } from '@directives/only-time.directive';
import { FlagCategory } from '@enums/service-request-flags.enum';
import { setCategoryColor } from '@helpers/service-request-flag.helper';
import {
  ServiceFlagTabData,
  ServiceRequestFlag,
  ServiceRequestFlagPayload,
  ServiceRequestFlagResponse,
} from '../interfaces/service-request-flag.interface';
import { NotificationService } from '@notification/service';

enum FlagType {
  Active = 'Active Flags',
  Resolved = 'Resolved Flags',
}

const imports = [
  OnlyTimeDirective,

  CommonModule,
  MatIconModule,
  MatInputModule,
  MatSelectModule,
  MatDialogModule,
  MatRippleModule,
  ReactiveFormsModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatProgressSpinnerModule,
  MatTableModule,
  SelectDropDownModule,
  MatTabsModule,
  DatePipe,
  MatTooltipModule,
];

const providers = [
  LocationV2Service,
  { provide: DateAdapter, useClass: NativeDateAdapter },
  { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
];

@Component({
  selector: 'app-service-request-flags',
  templateUrl: './service-request-flags.component.html',
  styleUrls: ['./service-request-flags.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: imports,
  providers: providers,
})
export class ServiceRequestFlagsComponent implements OnInit {
  flagTypes = FlagType;
  loader = true;
  isAddFlag = false;
  isUpdateFlag = false;
  isResolved = false;
  currentDate = new Date();
  minTime = '';
  isMaxFlagLimit = false;
  activeFlags: ServiceRequestFlag[] = [];
  resolvedFlags: ServiceRequestFlag[] = [];
  tabs: ServiceFlagTabData[] = [];

  activeFlagsDisplayedColumns: string[] = ['category', 'followUpTime', 'reason', 'actions'];
  resolvedFlagsDisplayedColumns: string[] = ['category', 'createdBy', 'resolvedBy', 'reason', 'resolutionNote'];
  activeFlagsDataSource: MatTableDataSource<ServiceRequestFlag>;
  resolvedFlagsDataSource: MatTableDataSource<ServiceRequestFlag>;

  createServiceRequestFlagForm = new FormGroup({
    category: new FormControl<{ id: number; categoryName: string } | undefined>(undefined, [Validators.required]),
    followUpDate: new FormControl(''),
    followUpTime: new FormControl('', [Validators.minLength(5)]),
    reason: new FormControl('', [Validators.required]),
  });

  resolutionNote = new FormControl('', [Validators.required]);

  serviceRequestFlagPayload: ServiceRequestFlagPayload = {
    serviceRequestId: '',
    category: '',
    reason: '',
    followUpTime: '',
    timezone: '',
    srFlagId: '',
    createdOn: '',
  };

  categoryList: { id: number; categoryName: string }[] = [
    { id: 1, categoryName: FlagCategory.Customer },
    { id: 2, categoryName: FlagCategory.Ground },
    { id: 2, categoryName: FlagCategory.Air },
    { id: 3, categoryName: FlagCategory.SRInfo },
    { id: 4, categoryName: FlagCategory.Documentation },
    { id: 5, categoryName: FlagCategory.Catering },
  ];

  categoryConfig = {
    displayKey: 'categoryName',
    search: true,
    placeholder: 'Select Category',
    height: '350px',
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ServiceRequestFlagsComponent>,
    private _serviceRequestService: ServiceRequestService,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.getServiceRequestFlags();
  }

  private getServiceRequestFlags(): void {
    this._serviceRequestService
      .getServiceRequestFlags(this.data.serviceRequestId)
      .pipe(take(1))
      .subscribe((serviceRequestFlags: ServiceRequestFlagResponse) => {
        const { activeFlags, resolvedFlags } = this.updateServiceRequestFlagData(serviceRequestFlags);
        this.activeFlags = this.sortByFollowUpAndCreatedOn(activeFlags || []) || [];
        this.resolvedFlags = resolvedFlags?.sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()) || [];
        this.activeFlagsDataSource = new MatTableDataSource(this.activeFlags);
        this.resolvedFlagsDataSource = new MatTableDataSource(this.resolvedFlags);
        this.setTabData();
        this.loader = false;
        this.isMaxFlagLimit = Boolean(this.activeFlags?.length + this.resolvedFlags?.length >= 10);
        this.cdr.detectChanges();
      });
  }

  /**
   * Sorts the given list of ServiceRequestFlag objects by their follow up time and created on date.
   * Follow up time is prioritized over created on date.
   * If a follow up time is null, it is placed after all non-null follow up times.
   * If a follow up time is in the past, it is placed before all non-past follow up times.
   * @param {ServiceRequestFlag[]} serviceRequestFlags - The list of ServiceRequestFlag objects to be sorted.
   * @returns {ServiceRequestFlag[]} - The sorted list of ServiceRequestFlag objects.
   **/
  private sortByFollowUpAndCreatedOn(serviceRequestFlags: ServiceRequestFlag[]): ServiceRequestFlag[] {
    const now = new Date();
    const followUpTimePassedDueActiveFlags = serviceRequestFlags
      ?.filter((item) => item.followUpTime === null || (item.followUpTime !== null && item.followUpTime < now))
      ?.sort((a, b) => {
        if (a.followUpTime !== null && b.followUpTime !== null) {
          const aFollowUp = new Date(a.followUpTime);
          const bFollowUp = new Date(b.followUpTime);

          const aIsPast = aFollowUp < now;
          const bIsPast = bFollowUp < now;
          if (aIsPast && !bIsPast) return -1;
          if (!aIsPast && bIsPast) return 1;
        }
        return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime();
      });

    const followUpTimeActiveFlags = serviceRequestFlags
      ?.filter((item) => item.followUpTime !== null && item.followUpTime > now)
      ?.sort((a, b) => {
        const aFollowUp = new Date(a.followUpTime);
        const bFollowUp = new Date(b.followUpTime);
        const aIsPast = aFollowUp < now;
        const bIsPast = bFollowUp < now;
        if (aIsPast && !bIsPast) return -1;
        if (!aIsPast && bIsPast) return 1;
        return 0;
      });

    return [...followUpTimePassedDueActiveFlags, ...followUpTimeActiveFlags];
  }

  /**
   * Updates the given list of ServiceRequestFlag objects by converting their follow up time and created on date to the current user's timezone.
   * If a follow up time is null, it is not converted.
   * If a follow up time is in the past, it is marked as passed due.
   * If a follow up time is not in the past, it is marked as not passed due.
   * @param {ServiceRequestFlagResponse} serviceRequestFlags - The list of ServiceRequestFlag objects to be updated.
   * @returns {activeFlags: ServiceRequestFlag[], resolvedFlags: ServiceRequestFlag[]} - The updated list of ServiceRequestFlag objects.
   */
  private updateServiceRequestFlagData(serviceRequestFlags: ServiceRequestFlagResponse): {
    activeFlags: ServiceRequestFlag[];
    resolvedFlags: ServiceRequestFlag[];
  } {
    const activeFlags = serviceRequestFlags.activeFlags?.map((flag) => {
      const local = new Date(flag.followUpTime);
      const utcFollowUpTime = this.tranferToLocalTimeZone(local, local);
      return {
        ...flag,
        createdOn: new Date(this.tranferToLocalTimeZone(new Date(flag.createdOn), this.currentDate)),
        followUpTime: flag.followUpTime ? utcFollowUpTime : null,
        resolvedOn: new Date(this.tranferToLocalTimeZone(new Date(flag.resolvedOn), this.currentDate)),
        isPassedDue: utcFollowUpTime < this.tranferToLocalTimeZone(this.currentDate, local),
        categoryColor: setCategoryColor(flag.category),
      };
    });
    const resolvedFlags = serviceRequestFlags.resolvedFlags?.map((flag) => {
      const local = new Date(flag.followUpTime);
      const utcFollowUpTime = this.tranferToLocalTimeZone(local, local);
      return {
        ...flag,
        createdOn: new Date(this.tranferToLocalTimeZone(new Date(flag.createdOn), this.currentDate)),
        followUpTime: flag.followUpTime ? utcFollowUpTime : null,
        resolvedOn: new Date(this.tranferToLocalTimeZone(new Date(flag.resolvedOn), this.currentDate)),
        categoryColor: setCategoryColor(flag.category),
      };
    });

    return { activeFlags, resolvedFlags };
  }

  private tranferToLocalTimeZone(date: Date, currentDate: Date): Date {
    return new Date(date.getTime() - currentDate.getTimezoneOffset() * 60000);
  }

  private setTabData(): void {
    this.tabs = [
      { id: 1, name: this.flagTypes.Active, count: this.activeFlags?.length || 0 },
      { id: 2, name: this.flagTypes.Resolved, count: this.resolvedFlags?.length || 0 },
    ];
  }

  private splitDateTime(followUpTime: string | null, value: string): string | null {
    if (followUpTime === null) {
      return null;
    }
    if (value === 'date') {
      return new Date(followUpTime).toISOString();
    }
    const date = new Date(followUpTime);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const time = `${hours}:${minutes}`;
    return time;
  }

  private resetServiceRequestFlagPayload(): void {
    this.serviceRequestFlagPayload = {
      serviceRequestId: '',
      category: '',
      reason: '',
      followUpTime: '',
      timezone: '',
      srFlagId: '',
    };
  }

  private convertDateTime(): string | null {
    const date = this.createServiceRequestFlagForm.get('followUpDate').value;
    if (date) {
      const time = this.createServiceRequestFlagForm.get('followUpTime').value;
      const combined = new Date(date);
      if (time) {
        const [hours, minutes] = time.split(':').map(Number);
        combined.setHours(hours);
        combined.setMinutes(minutes);
        combined.setSeconds(0);
        combined.setMilliseconds(0);
      }
      return combined.toISOString();
    }
    return null;
  }

  closeDialog(): void {
    this.dialogRef.close(this.data.notificationId);
  }

  resolveFlag(element: ServiceRequestFlagPayload): void {
    this.isResolved = true;
    this.serviceRequestFlagPayload.srFlagId = element.srFlagId;
    this.serviceRequestFlagPayload.category = element.category;
    this.serviceRequestFlagPayload.followUpTime = element.followUpTime;
    this.serviceRequestFlagPayload.reason = element.reason;
    this.serviceRequestFlagPayload.timezone = element.timezone;
    this.serviceRequestFlagPayload.categoryColor = setCategoryColor(element.category);
    this.serviceRequestFlagPayload.createdOn = element.createdOn;
  }

  addFlag(element?: ServiceRequestFlag): void {
    this.isAddFlag = true;
    this.isUpdateFlag = false;
    if (element) {
      this.isUpdateFlag = true;
      this.updateServiceRequestFlagForm(element);
    }
  }

  updateServiceRequestFlagForm(element): void {
    this.serviceRequestFlagPayload.serviceRequestId = element.serviceRequestId;
    this.serviceRequestFlagPayload.srFlagId = element.srFlagId;
    this.createServiceRequestFlagForm.setValue(
      {
        category: { id: 1, categoryName: element.category },
        followUpDate: this.splitDateTime(element.followUpTime, 'date'),
        followUpTime: this.splitDateTime(element.followUpTime, 'time'),
        reason: element.reason,
      },
      { emitEvent: false },
    );
  }

  returnToServiceFlag(): void {
    if (this.isAddFlag) {
      this.isAddFlag = false;
      this.createServiceRequestFlagForm.reset();
      this.createServiceRequestFlagForm.markAsUntouched();
      this.createServiceRequestFlagForm.markAsPristine();
      Object.values(this.createServiceRequestFlagForm.controls).forEach((control) => {
        control.markAsPristine();
        control.markAsUntouched();
      });
    }
    if (this.isResolved) {
      this.isResolved = false;
      this.resolutionNote.reset();
      this.resolutionNote.markAsUntouched();
    }
    this.resetServiceRequestFlagPayload();
  }

  resolveServiceRequestFlag(): void {
    if (this.resolutionNote.valid) {
      this._serviceRequestService
        .resolveServiceRequestFlag({ srFlagId: this.serviceRequestFlagPayload.srFlagId, resolutionNote: this.resolutionNote.value })
        .pipe(take(1))
        .subscribe(() => {
          this.getServiceRequestFlags();
          this.returnToServiceFlag();
        });
    } else {
      this.resolutionNote.markAsTouched();
    }
  }

  saveServiceRequestFlag(): void {
    if (this.createServiceRequestFlagForm.valid) {
      const followUpTime = this.convertDateTime();
      if (this.serviceRequestFlagPayload.srFlagId) {
        this._serviceRequestService
          .updateServiceRequestFlagFollowupTime({ srFlagId: this.serviceRequestFlagPayload.srFlagId, followUpTime: followUpTime })
          .pipe(take(1))
          .subscribe(
            () => {
              this.getServiceRequestFlags();
              this.returnToServiceFlag();
            },
            ({ error }) => {
              this.notificationService.warning(error?.errors[0] || 'Something went wrong! Please try again');
            },
          );
      } else {
        const category = this.createServiceRequestFlagForm.get('category')?.value;
        this.serviceRequestFlagPayload.followUpTime = followUpTime;
        this.serviceRequestFlagPayload.serviceRequestId = this.data.serviceRequestId;
        this.serviceRequestFlagPayload.category = category?.categoryName;
        this.serviceRequestFlagPayload.reason = this.createServiceRequestFlagForm.get('reason')?.value;
        this.serviceRequestFlagPayload.timezone = this.data.timezone;
        this._serviceRequestService
          .createServiceRequestFlag(this.serviceRequestFlagPayload)
          .pipe(take(1))
          .subscribe(
            () => {
              this.getServiceRequestFlags();
              this.returnToServiceFlag();
            },
            ({ error }) => {
              this.notificationService.warning(error?.errors[0] || 'Something went wrong! Please try again');
            },
          );
      }
    } else {
      this.createServiceRequestFlagForm.markAllAsTouched();
    }
  }
}
